import * as React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import { Query } from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { combinators } from 'modules/journey/components/RuleBuilder/RuleBlock/RuleBlock.utils';
import {
  getTopLevelCustomEvents,
  getEventFieldMappings,
  getIsSendEmailTriggerEnabled,
  getJourneyCurrentConversion,
  getIsJourneyFieldMappingsEnabled,
} from 'modules/workflows/ducks';
import { useFieldMappingsContext } from 'modules/workflows/context/FieldMappingsContext';
import { AND } from 'utils/combinatorTypes';
import isEqual from 'lodash/isEqual';
import { combineSystemWithCustomEvents } from '../RuleBlock/EventOccursRule';
import { RULE_TYPES } from '../constants';
import {
  ChildRule,
  ContainsCloningError,
  Rules,
  SendToJourneyMetadata,
  ServerReportedCloningError,
  ValidationErrors,
} from '../types';
import {
  getJourneyConversionRuleIdx,
  transformServerReportedCloningErrors,
  validateRules,
} from './useRuleBuilder.utils';
import { validateClonedRules } from './validateClonedRules';

const ACTIONS = {
  ADD_RULE: 'add_rule',
  DELETE_RULE: 'delete_rule',
  SET_CHILD_RULE_EVENT: 'set_child_rule_event',
  ADD_CHILD_RULE: 'add_child_rule',
  ADD_JOURNEY_CONVERSION_RULE: 'add_journey_conversion_rule',
  UPDATE_SEND_TO_JOURNEY: 'update_send_to_journey',
  UPDATE_CHILD_RULE: 'update_child_rule',
  DELETE_CHILD_RULE: 'delete_child_rule',
  SET_SEARCH_QUERIES: 'set_search_query',
  SET_CHILD_RULE_COMBINATOR: 'set_child_rule_combinator',
  SET_STATE: 'set_state',
};

type Action = any;

type Dispatch = (action: Action) => void;
export type State = { rules: Rules };
type RuleBuilderProviderProps = {
  children: React.ReactNode;
  rules: Rules;
  isClonedWithErrors: boolean;
  isLoading: boolean;
  serverReportedCloningErrors?: ServerReportedCloningError[];
  storeRules?: (rules: Rules) => void;
};

type RuleBuilderContextType = {
  state: State;
  setChildRuleEvent: (
    ruleIdx: number,
    childRuleIdx: number,
    eventName: string,
    ruleType: string,
  ) => void;
  setSearchQueries: (
    ruleIdx: number,
    childRuleIdx: number,
    searchQueries: Partial<Query>[],
  ) => void;
  deleteChildRule: (ruleIdx: number, childruleIdx: number) => void;
  addChildRule: (ruleIdx: number, ruleType: string) => void;
  addJourneyConversionRule: (
    ruleIdx: number,
    ruleType: string,
    eventName: string,
    journeyConversionId: number,
  ) => void;
  updateSendToJourney: (
    ruleIdx: number,
    sendToJourneyMetadata: SendToJourneyMetadata | null,
  ) => void;
  setChildRuleCombinator: (ruleIdx: number, childRuleIdx: number, combinator: string) => void;
  addRule: () => void;
  deleteRule: (ruleIdx: number) => void;
  isLoading: boolean;
  checkForValidationErrors: () => boolean;
  hasCloningErrors: boolean;
  validationErrors?: ValidationErrors;
  journeyConversionRuleIdx?: number;
  setRules: (rules: Rules) => void;
};

const RuleBuilderContext = React.createContext<RuleBuilderContextType | undefined>(undefined);

function ruleBuilderReducer(state: State, action: Action) {
  switch (action.type) {
    case ACTIONS.ADD_RULE: {
      const newState = cloneDeep(state);
      newState.rules.push({
        combinator: AND,
        childRules: [],
      });
      return newState;
    }
    case ACTIONS.DELETE_RULE: {
      const { ruleIdx } = action;
      const newState = cloneDeep(state);
      newState.rules.splice(ruleIdx, 1);
      return newState;
    }

    case ACTIONS.SET_CHILD_RULE_EVENT: {
      const { ruleIdx, childRuleIdx, eventName, ruleType } = action;
      const newState = cloneDeep(state);
      const childRule = newState?.rules[ruleIdx]?.childRules[childRuleIdx];
      if (childRule?.containsCloningError === true) {
        delete childRule.containsCloningError;
      }
      childRule.eventName = eventName;
      childRule.ruleType = ruleType;
      return newState;
    }
    case ACTIONS.ADD_CHILD_RULE: {
      const { ruleIdx, ruleType } = action;
      const newState = cloneDeep(state);
      newState.rules[ruleIdx].childRules.push({
        ruleType,
        searchQuery: { combinator: AND, searchQueries: [] },
      });
      return newState;
    }
    case ACTIONS.UPDATE_SEND_TO_JOURNEY: {
      const { ruleIdx, sendToJourneyMetadata } = action;
      const newState = cloneDeep(state);
      const ruleToUpdate = newState.rules[ruleIdx];
      const updatedRule = {
        ...ruleToUpdate,
        sendToJourneyMetadata,
      };
      newState.rules[ruleIdx] = updatedRule;
      return newState;
    }
    case ACTIONS.ADD_JOURNEY_CONVERSION_RULE: {
      const { ruleIdx, ruleType, eventName, journeyConversionId } = action;
      const newState = cloneDeep(state);
      const childRuleToAdd: ChildRule = {
        eventName,
        ruleType,
        searchQuery: { combinator: AND, searchQueries: [] },
        journeyConversionRuleMetadata: {
          journeyConversionId,
        },
      };

      if (newState.rules[ruleIdx].childRules.length === 0) {
        newState.rules[ruleIdx].childRules.push(childRuleToAdd);
      } else {
        newState.rules.push({
          combinator: AND,
          childRules: [childRuleToAdd],
        });
      }

      return newState;
    }
    case ACTIONS.DELETE_CHILD_RULE: {
      const { ruleIdx, childRuleIdx } = action;
      const newState = cloneDeep(state);
      newState.rules[ruleIdx].childRules.splice(childRuleIdx, 1);
      return newState;
    }
    case ACTIONS.SET_SEARCH_QUERIES: {
      const { ruleIdx, childRuleIdx, searchQueries } = action;
      const newState = cloneDeep(state);
      const hasCloningErrors =
        newState?.rules[ruleIdx]?.childRules[childRuleIdx]?.containsCloningError === true;

      // if there is a child rule flag but there are no query related cloning error flags, remove the child rule flag
      if (hasCloningErrors) {
        const hasNoQueryCloningErrors = searchQueries?.every(
          (q: Partial<Query> & ContainsCloningError) => q?.containsCloningError !== true,
        );
        if (hasNoQueryCloningErrors) {
          delete newState.rules[ruleIdx].childRules[childRuleIdx].containsCloningError;
        }
      }

      newState.rules[ruleIdx].childRules[childRuleIdx].searchQuery.searchQueries = searchQueries;

      // if rule is UserPropertyExitRule, set userField to the first search query
      const { ruleType } = newState.rules[ruleIdx].childRules[childRuleIdx];
      if (ruleType === RULE_TYPES.USER_PROPERTY) {
        newState.rules[ruleIdx].childRules[childRuleIdx].userField = searchQueries[0].field;
      }
      return newState;
    }

    case ACTIONS.SET_CHILD_RULE_COMBINATOR: {
      const { ruleIdx, childRuleIdx, combinator } = action;
      if (!combinators.includes(combinator)) return state;

      const newState = cloneDeep(state);
      newState.rules[ruleIdx].childRules[childRuleIdx].searchQuery.combinator = combinator;
      return newState;
    }

    case ACTIONS.SET_STATE: {
      const { state: newState } = action;
      return newState;
    }

    default: {
      return state;
    }
  }
}

const emptyRuleState = [
  {
    combinator: AND,
    childRules: [],
  },
];

function RuleBuilderProvider({
  children,
  storeRules,
  rules,
  isClonedWithErrors,
  isLoading,
  serverReportedCloningErrors,
}: RuleBuilderProviderProps) {
  const isSendEmailTriggerEnabled = useSelector(getIsSendEmailTriggerEnabled);
  const journeyConversion = useSelector(getJourneyCurrentConversion);
  const [firstLoad, setFirstLoad] = React.useState(true);

  const initialState = {
    rules: !isEmpty(rules) ? rules : emptyRuleState,
  };
  const [state, dispatchFunction]: [State, Dispatch] = React.useReducer(
    ruleBuilderReducer,
    initialState,
  );

  const journeyConversionRuleIdx = React.useMemo(() => {
    return getJourneyConversionRuleIdx(journeyConversion, state);
  }, [rules, state?.rules]);

  /*
    Since we are using a seperate store for the rule builder when using react hook form, we want to watch the rules and when they change we will call the
    callback so we can update the store.  We initialize the store with the rules at the time of the provider being mounted, so we block the first call it keeps the
    data not dirty. 
  */
  React.useEffect(() => {
    if (storeRules && !firstLoad && !isEqual(state?.rules, emptyRuleState)) {
      storeRules?.(state?.rules);
    }
    if (firstLoad) {
      setFirstLoad(false);
    }
  }, [state?.rules]);

  const allEvents = combineSystemWithCustomEvents(
    useSelector(getTopLevelCustomEvents),
    isSendEmailTriggerEnabled,
  );

  const isJourneyFieldMappingsEnabled = useSelector(getIsJourneyFieldMappingsEnabled);
  const { fieldMappings } = useFieldMappingsContext();
  const mappingsOld = useSelector(getEventFieldMappings) || {};
  const mappings = isJourneyFieldMappingsEnabled ? fieldMappings : mappingsOld;

  const [errors, setErrors] = React.useState({});
  const hasServerCloningErrors = React.useMemo(
    () => !isEmpty(serverReportedCloningErrors),
    [serverReportedCloningErrors],
  );

  // locally computed cloning errors. not computed if we have server errors
  const localCloningErrors = React.useMemo(
    () =>
      !hasServerCloningErrors && isClonedWithErrors
        ? validateClonedRules({ rules: state?.rules, allEvents, mappings })
        : {},
    [
      hasServerCloningErrors,
      isClonedWithErrors,
      validateClonedRules,
      state?.rules,
      allEvents,
      mappings,
    ],
  );

  const hasCloningErrors = React.useMemo(
    () => !isEmpty(localCloningErrors) || hasServerCloningErrors,
    [localCloningErrors, hasServerCloningErrors],
  );
  // server cloning errors take precedence over local cloning errors
  const currentCloningErrors = React.useMemo(
    () =>
      serverReportedCloningErrors &&
      transformServerReportedCloningErrors(serverReportedCloningErrors, state?.rules),
    [serverReportedCloningErrors, state?.rules],
  );
  const cloningErrors = hasServerCloningErrors ? currentCloningErrors : localCloningErrors;
  const validationErrors = hasCloningErrors ? cloningErrors : errors;

  const resetValidationErrors = () => {
    setErrors({});
  };

  const dispatch = (action: Action) => {
    dispatchFunction(action);
    resetValidationErrors();
  };

  const checkForValidationErrors = () => {
    const errorState = validateRules(state?.rules);
    setErrors(errorState);
    if (isEmpty(errorState)) {
      return false;
    }
    return true;
  };

  React.useEffect(() => {
    dispatch({
      type: ACTIONS.SET_STATE,
      state: initialState,
    });
  }, [JSON.stringify(rules)]);

  const setRules = (newRules: Rules) => {
    dispatch({
      type: ACTIONS.SET_STATE,
      state: { rules: !isEmpty(newRules) ? newRules : emptyRuleState },
    });
  };

  const addRule = () =>
    dispatch({
      type: ACTIONS.ADD_RULE,
    });

  const setChildRuleEvent = (
    ruleIdx: number,
    childRuleIdx: number,
    eventName: string,
    ruleType: string,
  ) =>
    dispatch({
      type: ACTIONS.SET_CHILD_RULE_EVENT,
      ruleIdx,
      childRuleIdx,
      eventName,
      ruleType,
    });
  const setSearchQueries = (
    ruleIdx: number,
    childRuleIdx: number,
    searchQueries: Partial<Query>[],
  ) =>
    dispatch({
      type: ACTIONS.SET_SEARCH_QUERIES,
      ruleIdx,
      childRuleIdx,
      searchQueries,
    });

  const deleteChildRule = (ruleIdx: number, childRuleIdx: number) =>
    dispatch({
      type: ACTIONS.DELETE_CHILD_RULE,
      ruleIdx,
      childRuleIdx,
    });

  const addChildRule = (ruleIdx: number, ruleType: string) =>
    dispatch({
      type: ACTIONS.ADD_CHILD_RULE,
      ruleIdx,
      ruleType,
    });

  const addJourneyConversionRule = (
    ruleIdx: number,
    ruleType: string,
    eventName: string,
    journeyConversionId: number,
  ) =>
    dispatch({
      type: ACTIONS.ADD_JOURNEY_CONVERSION_RULE,
      ruleIdx,
      ruleType,
      eventName,
      journeyConversionId,
    });

  const setChildRuleCombinator = (ruleIdx: number, childRuleIdx: number, combinator: string) =>
    dispatch({
      type: ACTIONS.SET_CHILD_RULE_COMBINATOR,
      ruleIdx,
      childRuleIdx,
      combinator,
    });

  const deleteRule = (ruleIdx: number) =>
    dispatch({
      type: ACTIONS.DELETE_RULE,
      ruleIdx,
    });

  const updateSendToJourney = (
    ruleIdx: number,
    sendToJourneyMetadata: SendToJourneyMetadata | null,
  ) => {
    dispatch({
      type: ACTIONS.UPDATE_SEND_TO_JOURNEY,
      ruleIdx,
      sendToJourneyMetadata,
    });
  };

  const value = {
    state,
    setRules,
    validationErrors,
    checkForValidationErrors,
    setChildRuleEvent,
    setSearchQueries,
    deleteChildRule,
    updateSendToJourney,
    addChildRule,
    addJourneyConversionRule,
    setChildRuleCombinator,
    addRule,
    deleteRule,
    isLoading,
    hasCloningErrors,
    journeyConversionRuleIdx,
  };
  return <RuleBuilderContext.Provider value={value}>{children}</RuleBuilderContext.Provider>;
}

function useRuleBuilder() {
  const context = React.useContext(RuleBuilderContext);
  if (context === undefined) {
    throw new Error('useRuleBuilder must be used within a RuleBuilderProvider');
  }
  return context;
}

export { RuleBuilderProvider, useRuleBuilder };
